import React, { useState, useEffect } from 'react';
import { Search, Calendar, Activity, AlertTriangle, CheckCircle, RefreshCw, X } from 'lucide-react';
import { auditAPI } from '../api';

const ACTION_MAP: Record<string, string> = {
  CREATE: 'Criação',
  UPDATE: 'Edição',
  DELETE: 'Exclusão',
  VIEW: 'Visualização',
  STATUS_CHANGE: 'Alteração de Status',
  ATTACHMENT_UPLOAD: 'Upload de Anexo',
  AI_REPROCESS: 'Reprocessamento IA',
  LOGIN: 'Login no Sistema',
  EXPORT: 'Exportação de Dados',
};

const ACTION_COLORS: Record<string, string> = {
  CREATE: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
  UPDATE: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800',
  DELETE: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800',
  VIEW: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700',
  STATUS_CHANGE: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border-purple-200 dark:border-purple-800',
  LOGIN: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800',
};

const FIELD_MAP: Record<string, string> = {
  isFavorite: 'Favorito',
  valueRequested: 'Valor Solicitado',
  valueApproved: 'Valor Aprovado',
  assignedUserId: 'Responsável Atribuído',
  status: 'Status',
  priority: 'Prioridade',
  title: 'Título',
  description: 'Descrição',
  internalNotes: 'Notas Internas',
  city: 'Cidade',
  themeArea: 'Área Temática',
  cabinet: 'Gabinete',
  solicitanteName: 'Solicitante',
};

export const AuditLogs: React.FC<{ usersList: any[] }> = ({ usersList }) => {
  const [logs, setLogs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    action: '',
    userId: '',
    criticalOnly: false,
  });

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const ITEMS_PER_PAGE = 30;

  // Debouce para a pesquisa de protocolo
  const [searchDebounced, setSearchDebounced] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(filters.search);
      setCurrentPage(1);
    }, 450);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Resetar página quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1);
  }, [filters.action, filters.userId, filters.criticalOnly]);

  const loadLogs = async () => {
    setIsLoading(true);
    try {
      // Mapear filtros avançados
      const params: any = { limit: ITEMS_PER_PAGE, page: currentPage };
      if (searchDebounced) params.search = searchDebounced;
      if (filters.action) params.action = filters.action;
      if (filters.userId) params.userId = filters.userId;
      
      const data = await auditAPI.list(params);
      setHasMore(data.length === ITEMS_PER_PAGE);
      
      // Filtrar ações críticas no frontend caso a checkbox esteja marcada
      let finalData = data;
      if (filters.criticalOnly) {
        const criticalActions = ['DELETE', 'STATUS_CHANGE']; // Extensível
        finalData = data.filter((l: any) => {
          if (criticalActions.includes(l.action)) return true;
          if (l.payload?.action?.includes('remove')) return true;
          
          if (l.payload?.fieldsChanged && Array.isArray(l.payload.fieldsChanged)) {
            return l.payload.fieldsChanged.some((c: any) => {
              const fieldName = typeof c === 'string' ? c : c.field;
              return ['priority', 'assignedUserId', 'valueRequested', 'valueApproved'].includes(fieldName);
            });
          }
          return false;
        });
      }

      setLogs(groupLogs(finalData));
    } catch (e) {
      console.error('Erro ao carregar auditoria', e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, [searchDebounced, filters.action, filters.userId, filters.criticalOnly, currentPage]);

  // Agrupa logs do mesmo user, mesma action, mesmo targetId ocorridos num intervalo de 10s
  const groupLogs = (rawLogs: any[]) => {
    if (!rawLogs.length) return [];
    
    // rawLogs já deve vir ordenado desc (mais recente primeiro)
    const grouped: any[] = [];
    let currentGroup = { ...rawLogs[0], children: [] as any[] };

    for (let i = 1; i < rawLogs.length; i++) {
      const log = rawLogs[i];
      const timeDiff = Math.abs(new Date(currentGroup.createdAt).getTime() - new Date(log.createdAt).getTime()) / 1000;
      
      if (
        timeDiff <= 10 &&
        currentGroup.userId === log.userId &&
        currentGroup.action === log.action &&
        currentGroup.targetId === log.targetId
      ) {
        // Agrupa as mudanças de payload se for UPDATE
        if (log.payload?.fieldsChanged && currentGroup.payload?.fieldsChanged) {
           // Mescla arrays
           currentGroup.payload.fieldsChanged = Array.from(new Set([...currentGroup.payload.fieldsChanged, ...log.payload.fieldsChanged]));
        }
        currentGroup.children.push(log);
      } else {
        grouped.push(currentGroup);
        currentGroup = { ...log, children: [] };
      }
    }
    grouped.push(currentGroup);
    return grouped;
  };

  return (
    <div className="space-y-6 text-left animate-in fade-in duration-200">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold tracking-tight font-sans flex items-center gap-2">
            <Activity className="w-6 h-6 text-indigo-500" />
            Auditoria e Rastreabilidade
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Acompanhe o histórico completo de visualizações, alterações e acessos no sistema.
          </p>
        </div>
        <button 
          onClick={loadLogs}
          className="p-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-xl transition-all"
        >
          <RefreshCw className={`w-4 h-4 text-slate-600 dark:text-slate-400 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* FILTROS */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 shadow-sm flex flex-wrap gap-4 items-center">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
          <input 
            type="text" 
            placeholder="Pesquisar por protocolo (ex: DEM-2026...)"
            className="w-full pl-9 pr-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:border-indigo-500"
            value={filters.search}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
          />
        </div>
        
        <select 
          className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-indigo-500"
          value={filters.action}
          onChange={(e) => setFilters(prev => ({ ...prev, action: e.target.value }))}
        >
          <option value="">Todas as Ações</option>
          {Object.entries(ACTION_MAP).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>

        <select 
          className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-indigo-500"
          value={filters.userId}
          onChange={(e) => setFilters(prev => ({ ...prev, userId: e.target.value }))}
        >
          <option value="">Todos os Usuários</option>
          {usersList.map(u => (
            <option key={u.id} value={u.id}>{u.name}</option>
          ))}
        </select>

        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-300 cursor-pointer">
          <input 
            type="checkbox" 
            className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            checked={filters.criticalOnly}
            onChange={(e) => setFilters(prev => ({ ...prev, criticalOnly: e.target.checked }))}
          />
          <AlertTriangle className="w-4 h-4 text-red-500" />
          Apenas Críticas
        </label>
        
        {(filters.search || filters.action || filters.userId || filters.criticalOnly) && (
          <button 
            onClick={() => setFilters({ search: '', action: '', userId: '', criticalOnly: false })}
            className="p-2 text-slate-400 hover:text-red-500 transition-colors"
            title="Limpar filtros"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* LISTAGEM */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="py-12 text-center text-slate-500 animate-pulse">Carregando auditoria...</div>
        ) : logs.length === 0 ? (
          <div className="py-12 text-center text-slate-500 italic bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl">
            Nenhum log encontrado para os filtros atuais.
          </div>
        ) : (
          logs.map((log) => {
            const userName = log.user?.name || 'Sistema / API';
            const actionLabel = ACTION_MAP[log.action] || log.action;
            const actionStyle = ACTION_COLORS[log.action] || ACTION_COLORS.VIEW;
            
            // Construir a frase
            let sentence = `${userName} executou ${actionLabel.toLowerCase()}`;
            if (log.targetProtocol) {
               sentence = `${userName} executou ${actionLabel.toLowerCase()} na demanda ${log.targetProtocol}`;
            } else if (log.targetTable) {
               sentence = `${userName} acessou a área de ${log.targetTable}`;
            }

            return (
              <div key={log.id} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-3">
                    <span className={`px-2.5 py-1 rounded-lg text-xs font-bold border ${actionStyle}`}>
                      {actionLabel}
                    </span>
                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                      {sentence}
                    </span>
                  </div>
                  <div className="text-right flex flex-col items-end">
                    <span className="text-xs font-mono font-medium text-slate-500 flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />
                      {new Date(log.createdAt).toLocaleString('pt-BR')}
                    </span>
                    {log.children?.length > 0 && (
                      <span className="text-[10px] text-indigo-500 mt-1 font-semibold">
                        (+{log.children.length} ações agrupadas em 10s)
                      </span>
                    )}
                  </div>
                </div>

                {/* Exibição Inteligente do Payload */}
                {log.payload && (
                  <div className="mt-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 border border-slate-100 dark:border-slate-800">
                    <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Detalhes da Ação</h4>
                    
                    {log.payload.fieldsChanged ? (
                      <ul className="space-y-1.5">
                        {log.payload.fieldsChanged.map((c: any, i: number) => {
                          const fieldName = typeof c === 'string' ? c : c.field;
                          const valBefore = c.before === null || c.before === undefined || c.before === '' ? 'Vazio' : String(c.before);
                          const valAfter = c.after === null || c.after === undefined || c.after === '' ? 'Vazio' : String(c.after);
                          return (
                            <li key={i} className="text-sm flex items-center gap-2">
                              <CheckCircle className="w-3.5 h-3.5 text-slate-300 dark:text-slate-600" />
                              <span className="font-semibold text-slate-700 dark:text-slate-300">
                                {FIELD_MAP[fieldName] || fieldName}
                              </span>
                              <span className="text-slate-400 text-xs">
                                {typeof c === 'string' ? 'foi alterado' : `de "${valBefore}" para "${valAfter}"`}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <pre className="text-xs text-slate-600 dark:text-slate-400 font-mono whitespace-pre-wrap break-all">
                        {JSON.stringify(log.payload, null, 2)}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Paginação Geral */}
      {logs.length > 0 && (
        <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-[#1a1c23] border border-slate-200/60 dark:border-slate-800/60 rounded-2xl sm:px-6 shadow-sm">
          <div className="flex-1 flex justify-between items-center">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1 || isLoading}
              className="px-4 py-2 border border-slate-300 dark:border-slate-700 text-xs font-semibold rounded-xl bg-white dark:bg-[#1f222b] text-slate-700 dark:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              &larr; Anterior
            </button>
            <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">
              Página {currentPage}
            </span>
            <button
              onClick={() => setCurrentPage(prev => prev + 1)}
              disabled={!hasMore || isLoading}
              className="px-4 py-2 border border-slate-300 dark:border-slate-700 text-xs font-semibold rounded-xl bg-white dark:bg-[#1f222b] text-slate-700 dark:text-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              Próximo &rarr;
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
